import { useEffect, useMemo, useRef, useState } from 'react'
import { generatePOEmail } from '../lib/poEmail.js'
import { AGENT_LABELS } from '../lib/agents.js'

// Bottom-sheet that shows a pre-formatted email body for a purchase
// order. Two ways it gets opened:
//   1. Right after Confirm & Send on the OrderConfirmModal — manager
//      copies and pastes into their email/SMS app
//   2. From the Recent Orders list later — manager re-grabs the email
//      because they forgot to send it / want to send a follow-up
//
// Read-only. Single primary action: Copy. Single dismiss action: Done.
export default function POEmailModal({
  open,
  onClose,
  po,
  lineItems,
  restaurantName,
}) {
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  const textareaRef = useRef(null)

  // Reset transient state on every open.
  useEffect(() => {
    if (open) {
      setCopied(false)
      setError(null)
    }
  }, [open])

  const emailBody = useMemo(() => {
    if (!po || !lineItems) return ''
    return generatePOEmail({ po, lineItems, restaurantName })
  }, [po, lineItems, restaurantName])

  if (!open || !po) return null

  async function handleCopy() {
    setError(null)
    try {
      // Modern path. Requires HTTPS + secure context.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(emailBody)
      } else if (textareaRef.current) {
        // Fallback: select + execCommand for older browsers.
        textareaRef.current.select()
        document.execCommand('copy')
      } else {
        throw new Error('Clipboard not available')
      }
      setCopied(true)
      // Keep "Copied!" visible for 2.5s before reverting.
      setTimeout(() => setCopied(false), 2500)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[POEmailModal] copy failed', e)
      setError('Could not copy automatically. Long-press the text below to select, then Copy.')
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Order email — ${po.supplier_name}`}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <div className="min-w-0">
              <div className="text-[11px] font-medium text-black/50">
                {AGENT_LABELS.reorderEmailDraft}
              </div>
              <h2 className="mt-0.5 text-xl font-medium truncate">
                Order email
                <span className="ml-2 font-normal text-base text-black/60">
                  · {po.supplier_name}
                </span>
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Close"
            >
              Close
            </button>
          </div>
          {error && (
            <div className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-3 px-4 pb-4">
          <p className="text-sm text-black/70">
            Copy this and paste into your email or text-message app to send
            to {po.supplier_name}. Edits to the text below stay here only —
            they don't change anything in the app.
          </p>

          <textarea
            ref={textareaRef}
            value={emailBody}
            // Manager can edit before copying — useful for last-minute
            // tweaks like "need by 7am instead of 9am". onChange is a
            // no-op so React doesn't fight the user's edits.
            onChange={() => { /* manager-editable, not persisted */ }}
            rows={Math.min(20, Math.max(8, emailBody.split('\n').length + 2))}
            spellCheck={false}
            className="w-full rounded-lg border border-black/15 px-3 py-3 text-sm font-mono leading-snug outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
          />

          <div className="text-xs text-black/55">
            Tip: long-press inside the box to select all, then Copy. Or
            use the button below.
          </div>
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="button"
            onClick={handleCopy}
            className={`min-h-btn w-full rounded-lg px-4 py-4 text-xl font-medium text-white shadow-sm transition ${
              copied
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            }`}
          >
            {copied ? '✓ Copied to clipboard' : 'Copy email'}
          </button>
        </div>
      </div>
    </div>
  )
}
